import { useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Rating from "@/components/base/Rating";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ApiState from "@/components/base/ApiState";
import { getTouristSpots } from "@/services/publicApi";
import { IMG } from "@/config/site";
import { OG_IMAGES } from "@/config/ogImages";
import { useApiResource } from "@/hooks/useApiResource";
import SpotMapPreview from "@/pages/tourist-spots/components/SpotMapPreview";
import type { TouristSeason } from "@/types";

const PAGE_SIZE = 9;
const ALL = "All";

/** Season buckets, each with a short cue so visitors instantly get the vibe. */
const SEASONS: { key: TouristSeason; label: string; icon: string }[] = [
  { key: "Monsoon", label: "Monsoon · waterfalls", icon: "ri-rainy-line" },
  { key: "Winter", label: "Winter · heritage", icon: "ri-snowy-line" },
  { key: "Summer", label: "Summer · wildlife & hills", icon: "ri-sun-line" },
  { key: "Year-round", label: "Year-round", icon: "ri-calendar-check-line" },
];

export default function TouristSpots() {
  const { data, loading, error, reload } = useApiResource(() => getTouristSpots(), []);
  const spots = useMemo(() => data ?? [], [data]);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeDistrict = searchParams.get("district") || ALL;
  const activeSeason = (searchParams.get("season") as TouristSeason | null) || ALL;
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const districts = useMemo(() => [ALL, ...Array.from(new Set(spots.map((s) => s.district)))], [spots]);

  const filtered = useMemo(
    () =>
      spots.filter(
        (s) => (activeDistrict === ALL || s.district === activeDistrict) && (activeSeason === ALL || s.season === activeSeason),
      ),
    [spots, activeDistrict, activeSeason],
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  );

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    document.getElementById("spots-grid")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeDistrict, activeSeason, currentPage]);

  /** Builds the query params for a given filter combination (omitting defaults). */
  const buildParams = (opts: { district?: string; season?: string; page?: number } = {}) => {
    const district = opts.district ?? activeDistrict;
    const season = opts.season ?? activeSeason;
    const nextPage = opts.page ?? 1;
    const qs = new URLSearchParams();
    if (district !== ALL) qs.set("district", district);
    if (season !== ALL) qs.set("season", season);
    if (nextPage > 1) qs.set("page", String(nextPage));
    return qs;
  };

  /** Crawlable, shareable query string for a filter combination. */
  const buildQuery = (opts: { district?: string; season?: string; page?: number } = {}) => {
    const q = buildParams(opts).toString();
    return q ? `?${q}` : "";
  };

  const changePage = (p: number) => {
    setSearchParams(buildParams({ page: p }), { replace: true });
  };

  // Canonical keeps filters but drops the pagination param.
  const canonicalQuery = buildQuery({ page: 1 });

  const seoTitle =
    activeDistrict !== ALL
      ? `Tourist Places in ${activeDistrict}, Karnataka`
      : activeSeason !== ALL
        ? `${activeSeason} Tourist Places in the Mysore Region`
        : "Tourist Places in Mysore, Mandya, Chamarajanagar & Kodagu";

  const seoDescription =
    activeDistrict !== ALL
      ? `Discover the best tourist places in ${activeDistrict} district, Karnataka — palaces, temples, waterfalls and wildlife with timings, entry fees and the best season to visit.${
          activeSeason !== ALL ? ` Showing ${activeSeason.toLowerCase()} highlights.` : ""
        }`
      : activeSeason !== ALL
        ? `Plan a ${activeSeason.toLowerCase()} trip across Karnataka — ${activeSeason === "Monsoon" ? "waterfalls and misty hill retreats" : activeSeason === "Winter" ? "heritage palaces and temple trails" : activeSeason === "Summer" ? "wildlife safaris and cool hill escapes" : "spots that are great all year"}.`
        : "Explore heritage palaces, hill temples, gardens and wildlife across the Mysore region district by district — Mysore, Mandya, Chamarajanagar and Kodagu (Coorg).";

  const chipBase = "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold cursor-pointer whitespace-nowrap";
  const chipOn = "bg-primary-500 text-foreground-950";
  const chipOff = "border border-background-300 text-foreground-700 hover:border-primary-400";

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={`tourist places Karnataka, ${activeDistrict !== ALL ? `${activeDistrict} tourist places` : "district wise tourist spots"}, ${
          activeSeason !== ALL ? `${activeSeason.toLowerCase()} places in Karnataka` : "best season to visit Karnataka"
        }, Mysore Palace, Coorg sightseeing, Bandipur safari`}
        path={`/tourist-spots${canonicalQuery}`}
        image={OG_IMAGES.touristSpots}
      />

      <div className="relative w-full">
        <div className="absolute inset-0">
          <img src={IMG.tourist} alt="Tourist spots around Mysore" title="Tourist spots Mysore Coorg" className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/55 to-foreground-950/80"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-20">
          <div className="[&_a]:text-background-200 [&_span]:text-background-200">
            <Breadcrumbs items={[{ label: "Tourist Spots" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold tracking-tight text-background-50">
            Tourist spots across the Mysore region — by district &amp; season
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-lg text-background-200 leading-relaxed">
            Planning a trip or showing guests around? Filter by district and the best season — chase monsoon waterfalls,
            winter heritage trails or summer wildlife — and browse what to see, timings and entry fees.
          </p>
        </div>
      </div>

      <div id="spots-grid" className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-12 scroll-mt-24">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">District</span>
            {districts.map((d) => (
              <Link
                key={d}
                to={`/tourist-spots${buildQuery({ district: d })}`}
                className={`${chipBase} ${activeDistrict === d ? chipOn : chipOff}`}
              >
                {d === ALL ? "All districts" : d}
              </Link>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-foreground-500">Best season</span>
            <Link to={`/tourist-spots${buildQuery({ season: ALL })}`} className={`${chipBase} ${activeSeason === ALL ? chipOn : chipOff}`}>
              All seasons
            </Link>
            {SEASONS.map((s) => (
              <Link
                key={s.key}
                to={`/tourist-spots${buildQuery({ season: s.key })}`}
                className={`${chipBase} ${activeSeason === s.key ? chipOn : chipOff}`}
              >
                <i className={s.icon}></i> {s.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            {loading ? (
              "Loading destinations…"
            ) : (
              <>
                <span className="font-semibold text-foreground-950">{filtered.length}</span>{" "}
                {filtered.length === 1 ? "destination" : "destinations"}
                {activeDistrict !== ALL ? ` in ${activeDistrict}` : ""}
                {activeSeason !== ALL ? ` · best in ${activeSeason.toLowerCase()}` : ""}
              </>
            )}
          </p>
          {!loading && totalPages > 1 && (
            <p className="text-sm text-foreground-500 whitespace-nowrap">
              Page {currentPage} of {totalPages}
            </p>
          )}
        </div>

        <div className="mt-6">
          <ApiState
            loading={loading}
            error={error}
            onRetry={reload}
            skeleton={
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-80 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
                ))}
              </div>
            }
            isEmpty={filtered.length === 0}
            empty={<EmptyState icon="ri-camera-lens-line" title="No spots found" description="Try a different district or season filter." />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {paged.map((s) => (
                <article key={s.id} className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
                  <Link to={`/spot/${s.slug}`} className="block h-52 w-full overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.name}
                      title={`${s.name} ${s.locationName}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </Link>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-900">
                          <i className="ri-map-pin-line"></i> {s.district}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-900">
                          <i className={SEASONS.find((x) => x.key === s.season)?.icon || "ri-calendar-line"}></i> {s.season}
                        </span>
                      </div>
                      <Rating value={s.rating} />
                    </div>
                    <h2 className="mt-2 font-heading text-lg font-bold text-foreground-950 leading-snug">
                      <Link to={`/spot/${s.slug}`} className="hover:text-primary-700">{s.name}</Link>
                    </h2>
                    <p className="mt-1 text-xs font-medium text-foreground-500">Near {s.locationName}</p>
                    <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600 leading-relaxed">{s.description}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-100 pt-3 text-xs text-foreground-600">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-time-line text-secondary-500"></i> {s.timings}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-ticket-2-line text-secondary-500"></i> {s.entryFee}
                      </span>
                    </div>
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-foreground-600">
                      <i className="ri-sun-line text-accent-600"></i>
                      <span>
                        <span className="font-semibold text-foreground-800">Best time to visit:</span> {s.bestTime}
                      </span>
                    </p>
                    <div className="mt-3">
                      <SpotMapPreview name={s.name} locationName={s.locationName} district={s.district} />
                    </div>
                    <Link
                      to={`/spot/${s.slug}`}
                      className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                    >
                      View details <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
          </ApiState>
        </div>
      </div>
    </>
  );
}